/* Ott-common */
import {
  PageEntity,
  PageEntityV4,
  PageEntityV5,
} from '@dtvgo/backend-service/normalize';

/* Utils */
import format from 'date-fns/format';
import { isCollection } from 'immutable';
import { FONT_BASE, SPACE, AgeRatingBR } from './constants';
import {
  SHOW_LOG_IN_ENVIRONMENTS,
  IS_CURABLE_BANNER_ACTIVE,
  USE_LAYOUT_PAGES_V4,
  isPagesV5Active,
  ENVIRONMENT,
} from './env';

export function generateRandomStringId() {
  return (Date.now() + Math.random() * 100).toString(32);
}

export function noOp() {}

export function getRem(pixels) {
  return pixels / FONT_BASE;
}

export function remToPx(rems) {
  return rems * FONT_BASE;
}

export function isInNLast(position, n, length) {
  return position >= length - n;
}

/**
 * @param {import('immutable').List} a
 * @param {import('immutable').List} b
 */
export function shallowEqualLists(a, b) {
  if (a === b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  if (a.size !== b.size) {
    return false;
  }

  if (a.size === 0 && b.size === 0) {
    return true;
  }

  const entries = a.entries();
  return (
    b.every((v, k) => {
      const entry = entries.next().value;
      return entry && entry[1] === v && entry[0] === k;
    }) && entries.next().done
  );
}

export function mergeDeepWithLists(a, b) {
  return a.mergeWith((oldVal, newVal) => {
    if (oldVal?.mergeWith) return mergeDeepWithLists(oldVal, newVal);

    if (isCollection(oldVal) && isCollection(newVal) && shallowEqualLists(oldVal, newVal)) {
      return oldVal;
    }

    return newVal;
  }, b);
}

export function capitalize(string) {
  if (typeof string !== 'string') return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function compareValues(key, order = 'asc') {
  return function innerSort(a, b) {
    if (!Object.hasOwn(a, key) || !Object.hasOwn(b, key)) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = (typeof a[key] === 'string')
      ? a[key].toUpperCase() : a[key];
    const varB = (typeof b[key] === 'string')
      ? b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return (
      (order === 'desc') ? (comparison * -1) : comparison
    );
  };
}

let supportsPassive = false;
try {
  const options = {};
  Object.defineProperty(options, 'passive', {
    get: () => {
      supportsPassive = true;
      return true;
    },
  });
  window.addEventListener('testPassive', undefined, options);
  window.removeEventListener('testPassive', undefined, options);
} catch (error) {
  console.error('Error while detecting passive event listener support', error);
}

export function isPassiveEventListenersSupported() {
  return supportsPassive;
}

export function isLeftButtonMouseEvent(mouseEvent) {
  return mouseEvent.buttons === 1 || mouseEvent.button === 0;
}

export function stopPropagation(event) {
  event.stopPropagation();
}

export function getMajorVersion(version) {
  return Number.parseInt(version?.split('.')[0], 10);
}

export function getBEMClassName(block, element, modifiers = {}) {
  const elementPart = element ? `__${element}` : '';
  const baseClassName = `dtv-html5-c-${block}${elementPart}`;
  const modifiersToApply = Object.keys(modifiers).filter((modifier) => modifiers[modifier]).map((modifier) => `${baseClassName}--${modifier}`);
  return [baseClassName, ...modifiersToApply].join(' ');
}

export function logOnScreen(...args) {
  const consoleElement = document.getElementById('console');
  if (consoleElement) {
    const div = document.createElement('div');
    div.innerHTML = `[${format(new Date(), 'HH:mm:ss.SSS')}]${SPACE}${args.map(JSON.stringify).join(SPACE)}`;
    consoleElement.appendChild(div);
    consoleElement.scrollTop = consoleElement.scrollHeight;
  }
}

export function closeApp() {
  window.close();
}

export function returnFalse() {
  return false;
}

export function encodeUserDataBase64(userData) {
  return encodeURIComponent(btoa(JSON.stringify(userData)));
}

/* TODO: this will be better if we had a custom logger that supports different type of logging
         and log levels so we can configure it by environment. Even more, we could add logOnScreen
         function to it */
export function appConsoleLog(...args) {
  if (SHOW_LOG_IN_ENVIRONMENTS.has(ENVIRONMENT)) {
    /* eslint no-console: ["error", { allow: ["log", "error"] }] -- this is our custom logger */
    console.log(...args);
  }
}

export function getIsPagesV4OrV5Active() {
  return USE_LAYOUT_PAGES_V4 || isPagesV5Active;
}

export function getIsCurableBannerActive() {
  // It should be removed from the code when pages v4 or v5 is the final version.
  return (USE_LAYOUT_PAGES_V4 || isPagesV5Active) && IS_CURABLE_BANNER_ACTIVE;
}

export function getPageEntityModel() {
  if (isPagesV5Active) return PageEntityV5;
  if (USE_LAYOUT_PAGES_V4) return PageEntityV4;
  return PageEntity;
}

export function getPagesVersionActive() {
  if (isPagesV5Active) return 'v5';
  if (USE_LAYOUT_PAGES_V4) return 'v4';
  return 'v3';
}

export function convertRemToPixels(rem) {
  return rem * 16;
}

export function getCookieByName(name) {
  const cookies = document.cookie.split(`${name}=`)[1];
  if (cookies) {
    return cookies.split(';')[0];
  }
  return '';
}

export function getProfileCustomAge(profileEdited, selectedProfile) {
  if (profileEdited) {
    return profileEdited?.profile_custom_age;
  }

  if (selectedProfile?.isMainProfile) {
    return AgeRatingBR.DEFAULT_AGE;
  }

  return selectedProfile?.ageRating;
}

export function getProfileUnratedContent(profileEdited, selectedProfile) {
  if (profileEdited) {
    return profileEdited?.profile_unrated_content?.toString();
  }

  if (selectedProfile?.isMainProfile) {
    return 'true';
  }

  return selectedProfile?.isNotRatedContentAllowed?.toString();
}

export function toSnakeCase(str) {
  try {
    return str
      ?.toLowerCase()
      ?.trim()
      ?.replace(/\s+/g, '_')
      ?.replace(/_+/g, '_');
  } catch (error) {
    console.error(`An error occurred while converting to snake case: ${error.message}`);
    return str;
  }
}

export function getValueAfterHyphen(input) {
  if (typeof input !== 'string') return input;

  const parts = input?.split('-');

  return parts?.length > 1 && parts[1] ? parts[1] : input;
}

export function getQueryParameters(url) {
  if (!url || typeof url !== 'string') return url;

  const queryString = url?.includes('?') ? url?.split('?')[1] : url;

  if (!queryString) return {};

  const searchParams = new URLSearchParams(queryString);
  const params = {};

  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }

  return params;
}
